import React from "react";
import { getBadgeClassName } from "../utils/recordMappers";

const badgeBaseClass =
  "inline-flex max-w-full items-center rounded-md px-2.5 py-1 text-left text-[11px] font-semibold leading-4 tracking-[0.02em] whitespace-normal break-words";

const checkboxIconClassName =
  "h-3 w-3 text-slate-950 opacity-0 transition-opacity duration-200 peer-checked:opacity-100";

const DatasetRecordTable = ({
  totalRecords,
  totalIsEstimated = true,
  summaryConfig,
  theme,
  rows,
  selectedRecordId,
  enableRowSelection = false,
  selectedRowIds = [],
  onToggleRowSelection,
  onToggleSelectAllRows,
  onRowClick,
  loading,
  error,
  query,
}) => {
  const checkboxVisualClassName = `flex h-5 w-5 items-center justify-center rounded-md border bg-slate-950/90 shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_8px_18px_rgba(2,6,23,0.22)] transition-all duration-200 peer-focus-visible:ring-2 peer-checked:bg-gradient-to-br ${theme.checkbox || "border-cyan-300/35 text-cyan-200 hover:border-cyan-200/55 hover:bg-sky-950/70 focus-visible:ring-cyan-300/25"} ${theme.checkboxChecked || "border-cyan-100/70 from-teal-300 to-sky-400 shadow-[0_0_0_1px_rgba(186,230,253,0.16),0_10px_22px_rgba(14,165,233,0.24)]"}`;
  const selectedRowIdSet = new Set(selectedRowIds);
  const hasRows = rows.length > 0;
  const allRowsSelected =
    enableRowSelection && hasRows && rows.every((row) => selectedRowIdSet.has(row.id));
  const someRowsSelected =
    enableRowSelection && rows.some((row) => selectedRowIdSet.has(row.id));

  const renderValue = (item) => {
    if (!item) {
      return <span className="text-slate-500">N/A</span>;
    }

    if (item.value === "N/A") {
      return <span className="text-sm text-slate-500">N/A</span>;
    }

    if (
      item.kind === "status" ||
      item.kind === "doc-status" ||
      item.kind === "accent" ||
      item.kind === "id"
    ) {
      return (
        <span className={`${badgeBaseClass} ${getBadgeClassName(item.kind, item.value, theme)}`}>
          {item.value}
        </span>
      );
    }

    return <span className="whitespace-pre-wrap break-words text-sm leading-5 text-slate-100">{item.value}</span>;
  };

  return (
    <section className= {`relative overflow-hidden rounded-[1.35rem] border bg-[#121924]/95 shadow-[0_24px_60px_rgba(2,6,23,0.55)] ${theme.detailBorder}`}>
      <div className={` border-b border-slate-800/90 px-5 py-4 bg-gradient-to-b ${theme.detailGlow} via-transparent to-transparent `}>
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
              Dataset Records
            </p>
            <p className="mt-1 text-sm text-slate-400">
              {typeof totalRecords === "number" ? (
                <>
                  {query
                    ? totalIsEstimated
                      ? "Approx Matching Results"
                      : "Matching Results"
                    : totalIsEstimated
                      ? "Approx Total Records"
                      : "Total Records"}
                  :{" "}
                  <span className={`font-semibold ${theme.subtleText}`}>{totalRecords}</span>
                </>
              ) : query ? (
                "Showing paginated search results"
              ) : (
                "Showing paginated dataset records"
              )}
            </p>
          </div>
          <div className="flex items-center gap-4">
            {enableRowSelection && hasRows ? (
              <label className="group inline-flex cursor-pointer items-center gap-2 text-xs text-slate-400">
                <input
                  type="checkbox"
                  checked={allRowsSelected}
                  ref={(input) => {
                    if (input) {
                      input.indeterminate = !allRowsSelected && someRowsSelected;
                    }
                  }}
                  onChange={() => onToggleSelectAllRows?.()}
                  className="peer sr-only"
                  aria-label="Select all visible rows"
                />
                <div className={checkboxVisualClassName} aria-hidden="true">
                  <svg
                    className={`${checkboxIconClassName} ${!allRowsSelected && someRowsSelected ? "opacity-100" : ""}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="3"
                  >
                    {!allRowsSelected && someRowsSelected ? (
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    )}
                  </svg>
                </div>
                <span>Select page</span>
              </label>
            ) : null}
            <p className="text-xs italic text-slate-500">Click a row to open the detail view</p>
          </div>
        </div>
      </div>

      <div className="max-w-full overflow-x-auto overscroll-x-contain">
        <div className="w-full min-w-[68rem]">
          <div className="border-b border-slate-800 bg-slate-950/60 px-5 py-3">
            <div className="flex gap-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              {enableRowSelection ? (
                <div className="w-8 shrink-0" aria-hidden="true" />
              ) : null}
              <div className="min-w-[15rem] flex-[2.55]">{summaryConfig.primary.label}</div>
              {summaryConfig.columns.map((column) => (
                <div key={column.label} className={`${column.className} min-w-0`}>
                  {column.label}
                </div>
              ))}
            </div>
          </div>

          <div className="divide-y divide-slate-800/80">
            {loading ? (
              <div className="flex min-h-[22rem] items-center justify-center px-5 py-10 text-sm text-slate-400">
                Loading records...
              </div>
            ) : error ? (
              <div className="px-5 py-8">
                <div className="rounded-2xl border border-rose-500/20 bg-rose-500/10 px-4 py-4 text-sm text-rose-100">
                  {error}
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div className="flex min-h-[22rem] items-center justify-center px-5 py-10 text-center text-sm text-slate-400">
                {query ? "No matching records were found for this dataset." : "No records are available yet."}
              </div>
            ) : (
              rows.map((row) => {
                const isActive = row.id === selectedRecordId;

                return (
                  <div
                    key={row.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => onRowClick(row.id)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onRowClick(row.id);
                      }
                    }}
                    className={`w-full px-5 py-4 text-left transition-colors hover:bg-slate-900/80 ${
                      isActive ? theme.selectedRow : ""
                    }`}
                  >
                    <div className="flex gap-3">
                      {enableRowSelection ? (
                        <div className="flex w-8 shrink-0 items-start pt-0.5">
                          <label
                            className="group inline-flex cursor-pointer items-center"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={selectedRowIdSet.has(row.id)}
                              onChange={() => onToggleRowSelection?.(row.id)}
                              onClick={(event) => event.stopPropagation()}
                              className="peer sr-only"
                              aria-label={`Select row ${row.primary.value}`}
                            />
                            <div className={checkboxVisualClassName} aria-hidden="true">
                              <svg
                                className={checkboxIconClassName}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth="3"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          </label>
                        </div>
                      ) : null}
                      <div className="min-w-[15rem] flex-[2.55]">
                        <p className="whitespace-pre-wrap break-words text-sm font-medium leading-5 text-slate-50">
                          {row.primary.value}
                        </p>
                        {row.secondary ? (
                          <p className="mt-1 whitespace-pre-wrap break-words text-xs font-medium tracking-[0.03em] text-slate-500">
                            {row.secondary.value}
                          </p>
                        ) : null}
                      </div>

                      {row.columns.map((column) => (
                        <div key={`${row.id}-${column.label}`} className={`${column.className} min-w-0`}>
                          {renderValue(column)}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DatasetRecordTable;
